// Zugriff auf die eigene API. Ersetzt das frühere window.api aus dem Electron-Preload –
// die Methodennamen sind absichtlich dieselben geblieben, damit die Oberfläche
// unverändert weiterverwendet werden konnte.
import { getApiToken } from './msal';

async function request(pathname, { method = 'GET', json, body, headers = {} } = {}) {
  const token = await getApiToken();

  const res = await fetch(`/api${pathname}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: json !== undefined ? JSON.stringify(json) : body,
  });

  if (!res.ok) {
    // Der Server formuliert seine Fehler für Endnutzer – die zeigen wir direkt an.
    let message = `Serverfehler ${res.status}`;
    try {
      const data = await res.json();
      if (data && data.error) message = data.error;
    } catch {
      /* keine JSON-Antwort (z. B. Gateway-Fehler) – Statuscode genügt */
    }
    throw new Error(message);
  }

  if (res.status === 204) return null;
  return res.json();
}

const binary = (buffer) => ({
  method: 'POST',
  body: buffer,
  headers: { 'Content-Type': 'application/octet-stream' },
});

export const api = {
  // --- Konto ---
  getMe: () => request('/me'),

  // --- Einstellungen ---
  // Der HubSpot-Zugang gilt für alle Nutzer und liegt in der Server-Konfiguration –
  // deshalb gibt es hier nur einen Verbindungstest, kein Speichern.
  getSettings: () => request('/settings'),
  testHubspotSettings: () => request('/settings/hubspot/test', { method: 'POST' }),
  saveStorageSettings: (values) => request('/settings/storage', { method: 'PUT', json: values }),
  setCoachPrompt: (text) => request('/settings/coach-prompt', { method: 'PUT', json: { text } }),

  // --- Aufnahmen ---
  // summaryQuery durchsucht den Protokolltext serverseitig – die Protokolle selbst
  // werden in der Übersicht nicht mitgeliefert.
  listRecordings: (summaryQuery = '') =>
    request(`/recordings${summaryQuery ? `?summary=${encodeURIComponent(summaryQuery)}` : ''}`),
  getRecording: (id) => request(`/recordings/${id}`),
  getTexts: (id) => request(`/recordings/${id}/texts`),

  beginRecording: (meeting) => request('/recordings', { method: 'POST', json: { meeting } }),
  writeChunk: (id, buffer) => request(`/recordings/${id}/chunk`, binary(buffer)),
  endRecording: (id, info) => request(`/recordings/${id}/end`, { method: 'POST', json: info }),

  // --- Verarbeitung ---
  transcribeRecording: (id) => request(`/recordings/${id}/transcribe`, { method: 'POST' }),
  summarizeRecording: (id) => request(`/recordings/${id}/summarize`, { method: 'POST' }),
  askTranscript: (id, history) => request(`/recordings/${id}/ask`, { method: 'POST', json: { history } }),

  // --- Live-Coach ---
  sendLiveSegment: (id, buffer) => request(`/recordings/${id}/live`, binary(buffer)),

  // --- HubSpot ---
  searchHubspotContacts: (query) => request(`/hubspot/contacts?q=${encodeURIComponent(query)}`),
  matchAttendees: (attendees) =>
    request('/hubspot/match-attendees', { method: 'POST', json: { attendees } }),
  setRecordingContacts: (id, contacts) =>
    request(`/recordings/${id}/contacts`, { method: 'PUT', json: { contacts } }),
  pushToHubspot: (id, contacts) =>
    request(`/recordings/${id}/hubspot`, { method: 'POST', json: { contacts } }),
  // Teilnehmer selbst in HubSpot suchen und das Protokoll eintragen – dasselbe,
  // was nach einer neuen Aufnahme automatisch passiert.
  autoPushToHubspot: (id) => request(`/recordings/${id}/hubspot/auto`, { method: 'POST' }),

  // Aufnahme von der HubSpot-Übertragung ausnehmen
  setHubspotSkip: (id, skip) =>
    request(`/recordings/${id}/hubspot/skip`, { method: 'PUT', json: { skip } }),

  // --- Sprecherzuordnung ---
  getSpeakers: (id) => request(`/recordings/${id}/speakers`),
  // Speichern löst bei vollständiger Zuordnung Protokoll-Neuerstellung und
  // HubSpot-Übertragung aus.
  saveSpeakers: (id, mapping) =>
    request(`/recordings/${id}/speakers`, { method: 'PUT', json: mapping }),
};
